import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import scheduledInterviewsApi from '../../../middleware/api/Recruitment/ScheduledInterviews/ScheduledInterviewsApi'
import { RootState } from '../../../stateStore'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import {
  GetSearchScheduledCandidatesProps,
  GetSearchScheduledCandidatesResponse,
  ScheduledInterviewsSliceState,
} from '../../../types/Recruitment/ScheduledInterviews/scheduledInterviewsTypes'
import { SelectedView } from '../../../types/TimeAndAttendance/TimeInOfficeReport/timeInOfficeReportTypes'

const getScheduledCandidatesForEmployee = createAsyncThunk(
  'scheduledInterviews/getScheduledCandidatesForEmployee',
  async (props: GetSearchScheduledCandidatesProps, thunkApi) => {
    try {
      return await scheduledInterviewsApi.getScheduledCandidatesForEmployee(
        props,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getScheduledCandidates = createAsyncThunk(
  'scheduledInterviews/getScheduledCandidates',
  async (props: GetSearchScheduledCandidatesProps, thunkApi) => {
    try {
      return await scheduledInterviewsApi.getScheduledCandidates(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialScheduledInterviewsSliceState: ScheduledInterviewsSliceState = {
  selectedView: 'Me',
  scheduledCandidatesForEmployee: { list: [], size: 0 },
  scheduledCandidates: { list: [], size: 0 },
  isLoading: ApiLoadingState.idle,
  isRetain: false,
  propObj: {
    startIndex: 0,
    endIndex: 20,
    fromDate: '',
    toDate: '',
    search: '',
    skill: '',
    internalExternalFlag: '',
  },
  propObjInterview: {
    startIndex: 0,
    endIndex: 20,
    fromDate: '',
    toDate: '',
    status: '',
  },
  technology: '',
  scheduledCandidatesFromDate: '',
  scheduledCandidatesToDate: '',
  scheduledInterviewFromDate: '',
  scheduledInterviewToDate: '',
  interviewStatus: 'pending',
  isRetainInterview: false,
  isFromOtherCandidate: false,
  isFromOtherInterview: false,
  isRadio: false,
  searchValue: '',
}

const scheduledInterviewsSlice = createSlice({
  name: 'scheduledInterviews',
  initialState: initialScheduledInterviewsSliceState,
  reducers: {
    setSelectedView: (state, action) => {
      state.selectedView = action.payload
    },
    clearScheduledCandidatesForEmployee: (state) => {
      state.scheduledCandidatesForEmployee = { list: [], size: 0 }
    },
    clearScheduledCandidates: (state) => {
      state.scheduledCandidates = { list: [], size: 0 }
    },
    setIsRetain: (state, action) => {
      state.isRetain = action.payload
    },
    setIsPropObj: (state, action) => {
      state.propObj = action.payload
    },
    setIsProbObjInterview: (state, action) => {
      state.propObjInterview = action.payload
    },
    setTechnology: (state, action) => {
      state.technology = action.payload
    },
    setScheduledCandidatesFromDate: (state, action) => {
      state.scheduledCandidatesFromDate = action.payload
    },
    setScheduledCandidatesToDate: (state, action) => {
      state.scheduledCandidatesToDate = action.payload
    },
    setScheduledInterviewFromDate: (state, action) => {
      state.scheduledInterviewFromDate = action.payload
    },
    setScheduledInterviewToDate: (state, action) => {
      state.scheduledInterviewToDate = action.payload
    },
    setInterviewStatus: (state, action) => {
      state.interviewStatus = action.payload
    },
    setIsRetainInterview: (state, action) => {
      state.isRetainInterview = action.payload
    },
    setIsFromOtherCandidate: (state, action) => {
      state.isFromOtherCandidate = action.payload
    },
    setIsFromOtherInterview: (state, action) => {
      state.isFromOtherInterview = action.payload
    },
    setIsFromRadio: (state, action) => {
      state.isRadio = action.payload
    },
    setSearch: (state, action) => {
      state.searchValue = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getScheduledCandidatesForEmployee.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.scheduledCandidatesForEmployee = action.payload
      })
      .addCase(getScheduledCandidates.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.scheduledCandidates = action.payload
      })
      .addMatcher(
        isAnyOf(
          getScheduledCandidates.pending,
          getScheduledCandidatesForEmployee.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.scheduledInterviews.isLoading

const scheduledCandidatesForEmployee = (
  state: RootState,
): GetSearchScheduledCandidatesResponse =>
  state.scheduledInterviews.scheduledCandidatesForEmployee

const scheduledCandidates = (
  state: RootState,
): GetSearchScheduledCandidatesResponse =>
  state.scheduledInterviews.scheduledCandidates

const selectedView = (state: RootState): SelectedView =>
  state.scheduledInterviews.selectedView

const scheduledCandidatesListSize = (state: RootState): number =>
  state.scheduledInterviews.scheduledCandidates.size

const scheduledCandidatesForEmployeeListSize = (state: RootState): number =>
  state.scheduledInterviews.scheduledCandidatesForEmployee.size

const getIsRetain = (state: RootState): boolean =>
  state.scheduledInterviews.isRetain

const getTechnology = (state: RootState): string =>
  state.scheduledInterviews.technology

const getInterviewStatus = (state: RootState): string =>
  state.scheduledInterviews.interviewStatus

const getFromDate = (state: RootState): string =>
  state.scheduledInterviews.scheduledCandidatesFromDate

const getToDate = (state: RootState): string =>
  state.scheduledInterviews.scheduledCandidatesToDate

const getFromDateInterview = (state: RootState): string =>
  state.scheduledInterviews.scheduledInterviewFromDate

const getToDateInterview = (state: RootState): string =>
  state.scheduledInterviews.scheduledInterviewToDate

const getIsRetainInterview = (state: RootState): boolean =>
  state.scheduledInterviews.isRetainInterview

const getIsFromOtherCandidate = (state: RootState): boolean =>
  state.scheduledInterviews.isFromOtherCandidate

const getIsFromOtherInterview = (state: RootState): boolean =>
  state.scheduledInterviews.isFromOtherInterview
const getIsRadioButton = (state: RootState): boolean =>
  state.scheduledInterviews.isRadio
const getIsSearch = (state: RootState): string =>
  state.scheduledInterviews.searchValue

const getPropbObj = (
  state: RootState,
): {
  startIndex: number
  endIndex: number
  fromDate: string
  toDate: string
  search: string
  skill: string
  internalExternalFlag: string
} => state.scheduledInterviews.propObj
const getPropbObjInterview = (
  state: RootState,
): {
  startIndex: number
  endIndex: number
  fromDate: string
  toDate: string
  status: string
} => state.scheduledInterviews.propObjInterview

const scheduledInterviewsThunk = {
  getScheduledCandidatesForEmployee,
  getScheduledCandidates,
}

const scheduledInterviewsSelectors = {
  isLoading,
  scheduledCandidatesForEmployee,
  scheduledCandidates,
  scheduledCandidatesListSize,
  scheduledCandidatesForEmployeeListSize,
  selectedView,
  getIsRetain,
  getPropbObj,
  getTechnology,
  getFromDate,
  getToDate,
  getFromDateInterview,
  getToDateInterview,
  getInterviewStatus,
  getPropbObjInterview,
  getIsRetainInterview,
  getIsFromOtherCandidate,
  getIsFromOtherInterview,
  getIsRadioButton,
  getIsSearch,
}

export const scheduledInterviewsService = {
  ...scheduledInterviewsThunk,
  actions: scheduledInterviewsSlice.actions,
  selectors: scheduledInterviewsSelectors,
}

export default scheduledInterviewsSlice.reducer
